import type RestApiClient from '$lib/api/restApi';
import type { HubsAPI, Post } from '$lib/api/rpc';
import { isPostSettingsModalOpen } from '$lib/components/Post/Settings/settingsModalStore';
import { get, type Writable } from 'svelte/store';

export let selectedPost: Writable<Post>;

export const toggleLike = async (api: HubsAPI, postStore: Writable<Post>) => {
	const post = get(postStore);
	if (!post.user_context) return;
	const postIsLiked = post.user_context.liked;
	postStore.update((post) => {
		if (!post.user_context) return post;
		post.like_count += postIsLiked ? -1 : 1;
		post.user_context.liked = !postIsLiked;
		return post;
	});
	if (!postIsLiked) {
		await api.likePost({ hubId: post.hub_id, postId: post.id });
	} else {
		await api.unlikePost({ hubId: post.hub_id, postId: post.id });
	}
};

export const togglePin = async (
	postIndex: number | undefined,
	restApi: RestApiClient,
	pinnedPosts: Writable<Post[]>,
	postStore: Writable<Post>,
	pinned: boolean
) => {
	if (postIndex === undefined) return;
	const post = get(postStore);

	postStore.update((post) => {
		post.offset = pinned ? 0 : postIndex;
		return post;
	});
	pinnedPosts.update((pinnedPosts) => {
		if (pinned) {
			return pinnedPosts.filter((pinned) => pinned.id != post.id);
		} else {
			return [...pinnedPosts, post];
		}
	});
	if (pinned) {
		await restApi.unpinPost(post.hub_id, post.id);
	} else {
		await restApi.pinPost(post.hub_id, post.id, postIndex);
	}
};

export const toggleFeature = async (
	api: HubsAPI,
	featuredPostsStore: Writable<Post[]>,
	postStore: Writable<Post>
) => {
	const post = get(postStore);
	const featuredPosts = get(featuredPostsStore);
	const postIsFeatured = featuredPosts.find((featured) => featured.id == post.id);
	featuredPostsStore.update((featuredPosts) => {
		if (postIsFeatured) {
			return featuredPosts.filter((featured) => featured.id != post.id);
		} else {
			return [...featuredPosts, post];
		}
	});
	if (postIsFeatured) {
		await api.unfeaturePost({ hubId: post.hub_id, postId: post.id });
	} else {
		await api.featurePost({ hubId: post.hub_id, postId: post.id });
	}
};

export const toggleHide = async (
	restApi: RestApiClient,
	postsStore: Writable<Post[]>,
	postStore: Writable<Post>,
	pinnedPosts?: Writable<Post[]>,
	featuredPosts?: Writable<Post[]>
) => {
	const post = get(postStore);
	const postIsHidden = post.status == 'hidden';

	postStore.update((post) => {
		post.status = postIsHidden ? 'published' : 'hidden';
		return post;
	});

	if (postIsHidden) {
		await restApi.unhidePost(post.hub_id, post.id);
	} else {
		await restApi.hidePost(post.hub_id, post.id);
	}

	postsStore.update((posts) => {
		if (postIsHidden) {
			return [...posts, post];
		} else {
			return posts.filter((p) => {
				return p.id != post.id;
			});
		}
	});

	if (featuredPosts) {
		featuredPosts.update((posts) => {
			if (postIsHidden) {
				return [...posts, post];
			} else {
				return posts.filter((p) => {
					return p.id != post.id;
				});
			}
		});
	}

	if (pinnedPosts) {
		pinnedPosts.update((posts) => {
			if (postIsHidden) {
				return [...posts, post];
			} else {
				return posts.filter((p) => {
					return p.id != post.id;
				});
			}
		});
	}
};

export const openSettingsModal = async (api: HubsAPI, postStore: Writable<Post>) => {
	const post = get(postStore);
	const resp = await api.getPost({ hubId: post.hub_id, postId: post.id });
	postStore.set(resp.post);
	selectedPost = postStore;
	isPostSettingsModalOpen.set(true);
};
